import { DragEvent, useCallback, useEffect, useRef } from 'react'
import { MRT_RowData, MRT_TableInstance } from 'material-react-table'

const EDGE_ZONE_WIDTH = 60
const MAX_SCROLL_STEP = 20

function getScrollStep(cursorX: number, leftEdge: number, rightEdge: number) {
  if (cursorX < leftEdge || cursorX > rightEdge) return 0

  const rightZoneStart = rightEdge - EDGE_ZONE_WIDTH
  if (cursorX > rightZoneStart) {
    const depth = (cursorX - rightZoneStart) / EDGE_ZONE_WIDTH
    return Math.ceil(MAX_SCROLL_STEP * depth)
  }
  const leftZoneEnd = leftEdge + EDGE_ZONE_WIDTH
  if (cursorX < leftZoneEnd) {
    const depth = (leftZoneEnd - cursorX) / EDGE_ZONE_WIDTH
    return -Math.ceil(MAX_SCROLL_STEP * depth)
  }
  return 0
}

export default function useColumnDragAutoScroll<
  DataType extends MRT_RowData,
>() {
  const containerRef = useRef<HTMLElement | null>(null)
  const scrollStepRef = useRef(0)
  const frameIdRef = useRef<number | null>(null)

  const stopScrolling = useCallback(() => {
    scrollStepRef.current = 0
    if (frameIdRef.current !== null) {
      cancelAnimationFrame(frameIdRef.current)
      frameIdRef.current = null
    }
    document.removeEventListener('dragend', stopScrolling)
    document.removeEventListener('drop', stopScrolling)
  }, [])

  const scrollOneFrame = useCallback(() => {
    const container = containerRef.current
    if (!container || scrollStepRef.current === 0) {
      frameIdRef.current = null
      return
    }
    container.scrollLeft += scrollStepRef.current
    frameIdRef.current = requestAnimationFrame(scrollOneFrame)
  }, [])

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>, table: MRT_TableInstance<DataType>) => {
      if (!table.getState().draggingColumn) {
        stopScrolling()
        return
      }

      const container = event.currentTarget
      const { left } = container.getBoundingClientRect()
      scrollStepRef.current = getScrollStep(
        event.clientX,
        left + table.getLeftTotalSize(),
        left + container.clientWidth - table.getRightTotalSize(),
      )
      if (scrollStepRef.current === 0) {
        stopScrolling()
        return
      }

      containerRef.current = container
      if (frameIdRef.current === null) {
        document.addEventListener('dragend', stopScrolling)
        document.addEventListener('drop', stopScrolling)
        frameIdRef.current = requestAnimationFrame(scrollOneFrame)
      }
    },
    [scrollOneFrame, stopScrolling],
  )

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      const enteredElement = event.relatedTarget as Node | null
      if (!enteredElement || !event.currentTarget.contains(enteredElement)) {
        stopScrolling()
      }
    },
    [stopScrolling],
  )

  useEffect(() => stopScrolling, [stopScrolling])

  return { handleDragOver, handleDragLeave }
}
